import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import MobileTopBar from '../components/MobileTopBar';
import { Sliders, Users, Cpu, CheckCircle, Smile, Percent, Clock, DollarSign, Monitor, Smartphone, Loader2 } from 'lucide-react';
import FiltersDrawer from '../components/FiltersDrawer';
import { StatCard, EmptyChartCard } from '../components/analytics/cards';
import { analyticsApi } from '../lib/api';

const DASH = '—';

function formatNA(v) {
  if (v === null || v === undefined) return 'N/A';
  return String(v);
}

function buildRows(d) {
  const r1 = [
    { Icon: Users, label: 'Total Applications', value: d ? d.total_applications : DASH },
    { Icon: Cpu, label: 'AI Interviews Completed', value: d ? d.ai_interviews_completed : DASH },
    { Icon: CheckCircle, label: 'Talent with above 70% match score', value: d ? d.talent_above_70 : DASH },
  ];
  const r2 = [
    { Icon: Smile, label: 'NPS score', value: d ? formatNA(d.nps) : 'N/A' },
    { Icon: Percent, label: 'Ratio (Invited → Completed)', value: d ? formatNA(d.ratio_invited_to_completed) : 'N/A' },
    { Icon: Percent, label: 'Ratio (Started → Completed)', value: d ? formatNA(d.ratio_started_to_completed) : 'N/A' },
    { Icon: Cpu, label: 'Invite → Interview Start', value: d ? formatNA(d.ratio_invited_to_started) : 'N/A' },
  ];
  const r3 = [
    { Icon: Clock, label: 'Time Saved', value: d ? d.time_saved_hours : DASH, suffix: 'h' },
    { Icon: DollarSign, label: 'Cost saved', value: d ? d.cost_saved_usd : DASH, suffix: 'USD' },
    { Icon: Monitor, label: 'Interviews done on desktop', value: d ? d.interviews_on_desktop : DASH },
    { Icon: Smartphone, label: 'Interviews done on Mobile', value: d ? d.interviews_on_mobile : DASH },
  ];
  return { r1, r2, r3 };
}

function AnalyticsHeader({ onOpenFilters }) {
  return (
    <div className="px-5 md:px-10 pt-6 md:pt-8 pb-5 flex items-center justify-between gap-3">
      <h1 className="text-[22px] md:text-[28px] font-bold text-[#1A1533] tracking-tight">Analytics dashboard</h1>
      <button
        onClick={onOpenFilters}
        className="inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full bg-white border border-[#E5DEF9] text-[#1A1533] text-[13px] font-semibold hover:bg-[#F1ECFE] transition-colors shrink-0"
      >
        <Sliders size={14} className="text-[#5B4FE8]" />
        <span className="hidden sm:inline">Filters</span>
      </button>
    </div>
  );
}

function StatRow({ items, cols }) {
  const colClass = cols === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4';
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${colClass} gap-3 md:gap-4`}>
      {items.map((it) => <StatCard key={it.label} {...it} />)}
    </div>
  );
}

export default function Analytics() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsApi.summary()
      .then((data) => setSummary(data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, []);

  const { r1, r2, r3 } = buildRows(summary);

  return (
    <div className="flex min-h-screen bg-[#F7F5FF]">
      <Sidebar />
      <main className="flex-1 flex flex-col overflow-x-hidden min-w-0">
        <MobileTopBar title="Analytics" />
        <AnalyticsHeader onOpenFilters={() => setFiltersOpen(true)} />

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-[#5B4FE8]" size={28} /></div>
        ) : (
          <div className="px-5 md:px-10 pb-10 space-y-4">
            <StatRow items={r1} cols={3} />
            <StatRow items={r2} cols={4} />
            <StatRow items={r3} cols={4} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
              <EmptyChartCard title="Total applicants" />
              <EmptyChartCard title="Total AI interviews" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
              <EmptyChartCard title="Candidates by role" showTabs />
              <EmptyChartCard title="Candidates by country" showTabs />
            </div>
          </div>
        )}
      </main>

      <FiltersDrawer open={filtersOpen} onOpenChange={setFiltersOpen} />
    </div>
  );
}
